import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useGymBillingOptional } from '../../contexts/GymBillingContext';
import { useTranslation } from 'react-i18next';
import { UserPlus, User, Users, Save, X, Copy, Check, Key, BookOpen, Search, Mail, RefreshCw, AlertCircle, CheckCircle, MessageCircle, Send, ArrowLeft, Tag } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';
import { getEmailApiBaseUrl } from '../../utils/emailApi';
import { openWhatsApp, normalizePhoneForWhatsApp } from '../../utils/whatsApp';

// Link app (WEBAPP-015) - stesso uso di ResetPasswordModal
const APP_LINK_ANDROID = 'https://play.google.com/store/apps/details?id=com.fitgymtrack.app';
const APP_LINK_IOS = 'https://apps.apple.com/app/fitgymtrack/id647906686';

// Prefissi telefono (stesso elenco di EditClientProfileModal)
const PHONE_PREFIXES = [
    { code: '39', label: '+39 Italia' },
    { code: '33', label: '+33 Francia' },
    { code: '34', label: '+34 Spagna' },
    { code: '44', label: '+44 Regno Unito' },
    { code: '41', label: '+41 Svizzera' },
    { code: '43', label: '+43 Austria' },
    { code: '49', label: '+49 Germania' },
    { code: '32', label: '+32 Belgio' },
    { code: '31', label: '+31 Paesi Bassi' },
    { code: '351', label: '+351 Portogallo' },
    { code: '30', label: '+30 Grecia' },
    { code: '48', label: '+48 Polonia' },
    { code: '46', label: '+46 Svezia' },
    { code: '47', label: '+47 Norvegia' },
    { code: '45', label: '+45 Danimarca' },
    { code: '358', label: '+358 Finlandia' },
    { code: '353', label: '+353 Irlanda' },
    { code: '352', label: '+352 Lussemburgo' },
    { code: '356', label: '+356 Malta' },
    { code: '386', label: '+386 Slovenia' },
    { code: '385', label: '+385 Croazia' },
    { code: '420', label: '+420 Rep. Ceca' },
    { code: '421', label: '+421 Slovacchia' },
    { code: '36', label: '+36 Ungheria' },
    { code: '40', label: '+40 Romania' },
    { code: '359', label: '+359 Bulgaria' },
    { code: '355', label: '+355 Albania' },
    { code: '389', label: '+389 Macedonia' },
    { code: '381', label: '+381 Serbia' },
    { code: '383', label: '+383 Kosovo' },
    { code: '387', label: '+387 Bosnia' },
    { code: '382', label: '+382 Montenegro' },
    { code: '1', label: '+1 USA/Canada' },
    { code: '7', label: '+7 Russia' },
    { code: '90', label: '+90 Turchia' },
    { code: '212', label: '+212 Marocco' },
    { code: '213', label: '+213 Algeria' },
    { code: '216', label: '+216 Tunisia' },
    { code: '20', label: '+20 Egitto' },
    { code: 'other', label: 'Altro' }
];

export default function AddMember() {
    const { getAuthenticatedApi } = useAuth();
    const { refreshBilling } = useGymBillingOptional() ?? {};
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        email: '',
        name: '',
        phonePrefix: '39',
        phoneNumber: '',
        phoneOtherPrefix: '',
        role_in_gym: 'member', // 'member' o 'trainer'
        marketingOptIn: false,
        canManageOwnWorkouts: true,
    });
    const [marketingTagIds, setMarketingTagIds] = useState([]);
    const [customEmailTags, setCustomEmailTags] = useState([]);
    const [loadingEmailTags, setLoadingEmailTags] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [createdUser, setCreatedUser] = useState(null);
    const [copied, setCopied] = useState(false);
    const [copiedMessage, setCopiedMessage] = useState(false);
    const [resendingEmail, setResendingEmail] = useState(false);

    const credentialsMessage = useMemo(() => {
        if (!createdUser?.temp_password || !createdUser?.username) return '';
        return t('gym.resetPasswordModal.credentialsMessage', {
            username: createdUser.username,
            password: createdUser.temp_password,
            linkAndroid: APP_LINK_ANDROID,
            linkIos: APP_LINK_IOS
        });
    }, [createdUser, t]);

    // Rubrica state
    const [rubricaContacts, setRubricaContacts] = useState([]);
    const [loadingRubrica, setLoadingRubrica] = useState(false);
    const [selectedContactId, setSelectedContactId] = useState('');
    const emailApi = getEmailApiBaseUrl();

    useEffect(() => {
        if (formData.role_in_gym !== 'member' || !formData.marketingOptIn) {
            setCustomEmailTags([]);
            setMarketingTagIds([]);
            return;
        }

        let cancelled = false;
        (async () => {
            setLoadingEmailTags(true);
            try {
                const api = getAuthenticatedApi();
                const res = await api.get('gym_email_tags.php?action=list', { baseURL: emailApi });
                if (!cancelled) {
                    const custom = (res.data?.tags || []).filter((tag) => !tag.is_system);
                    setCustomEmailTags(custom);
                }
            } catch {
                if (!cancelled) {
                    setCustomEmailTags([]);
                }
            } finally {
                if (!cancelled) {
                    setLoadingEmailTags(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [formData.role_in_gym, formData.marketingOptIn, getAuthenticatedApi, emailApi]);
    const [searchFilter, setSearchFilter] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);
    
    // Chiudi dropdown quando si clicca fuori
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        
        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showDropdown]);

    // Carica contatti rubrica all'avvio
    useEffect(() => {
        loadRubricaContacts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Solo all'avvio

    const loadRubricaContacts = async () => {
        setLoadingRubrica(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.get('/rubrica_api.php', {
                params: { action: 'list' }
            });
            
            if (response.data.success) {
                setRubricaContacts(response.data.data.contacts || []);
            }
        } catch (err) {
            console.error('Errore caricamento rubrica:', err);
            // Non mostriamo errore, la rubrica è opzionale
        } finally {
            setLoadingRubrica(false);
        }
    };

    // Handle selezione contatto rubrica
    const handleContactSelect = (contact) => {
        setSelectedContactId(contact.id.toString());
        
        // Autocompila nome (nome + cognome) e email
        const fullName = [contact.nome, contact.cognome]
            .filter(Boolean)
            .join(' ')
            .trim();
        
        setFormData(prev => ({
            ...prev,
            name: fullName,
            email: contact.email || ''
        }));
        
        // Reset filtro e chiudi dropdown
        setSearchFilter('');
        setShowDropdown(false);
    };
    
    // Filtra contatti in base alla ricerca
    const getFilteredContacts = () => {
        if (!searchFilter.trim()) return rubricaContacts;
        
        const searchLower = searchFilter.toLowerCase();
        return rubricaContacts.filter((contact) => {
            const fullName = [contact.nome, contact.cognome]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            const email = (contact.email || '').toLowerCase();
            return fullName.includes(searchLower) || email.includes(searchLower);
        });
    };
    
    const filteredContacts = getFilteredContacts();

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const generateUsername = (fullName) => {
        // Converte "Mario Rossi" in "mario.rossi"
        const cleanName = fullName.toLowerCase()
            .replace(/[^a-z\s]/g, '') // Rimuove caratteri speciali
            .trim()
            .split(' ')
            .filter(word => word.length > 0)
            .join('.');
        return cleanName;
    };

    const buildPhone = () => {
        const prefixDigits = formData.phonePrefix === 'other'
            ? (formData.phoneOtherPrefix || '').replace(/\D/g, '')
            : formData.phonePrefix;
        const numberDigits = (formData.phoneNumber || '').replace(/\D/g, '');
        return (prefixDigits + numberDigits).trim() || null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const phone = buildPhone() || null;
        if (formData.phonePrefix === 'other' && (formData.phoneNumber || formData.phoneOtherPrefix)) {
            const prefixDigits = (formData.phoneOtherPrefix || '').replace(/\D/g, '');
            if (!prefixDigits.trim()) {
                toast.error(t('gym.addMember.form.phonePrefixRequired'));
                return;
            }
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const generatedUsername = generateUsername(formData.name);
            const tempPassword = 'TEMP_' + Math.random().toString(36).slice(2, 10);

            const api = getAuthenticatedApi();
            const payload = {
                username: generatedUsername,
                name: formData.name,
                email: formData.email,
                password: tempPassword,
                phone,
                role_in_gym: formData.role_in_gym,
            };
            if (formData.role_in_gym === 'member') {
                payload.marketing_opt_in = formData.marketingOptIn ? 1 : 0;
                payload.can_manage_own_workouts = formData.canManageOwnWorkouts ? 1 : 0;
                if (formData.marketingOptIn && marketingTagIds.length > 0) {
                    payload.marketing_tag_ids = marketingTagIds;
                }
            }
            const response = await api.post('/gym_members.php?action=create', payload);
            
            const successKey = formData.role_in_gym === 'trainer' ? 'trainerCreated' : 
                              formData.role_in_gym === 'manager' ? 'managerCreated' : 'memberCreated';
            setSuccess(t(`gym.addMember.success.${successKey}`));
            
            setCreatedUser({
                name: formData.name,
                username: generatedUsername,
                email: formData.email,
                phone: response.data.user?.phone ?? phone,
                temp_password: response.data.temp_password,
                email_sent: response.data.email_sent || false,
                email_error: response.data.email_error || null,
                user_id: response.data.user?.id
            });
            
            setFormData({
                email: '',
                name: '',
                phonePrefix: '39',
                phoneNumber: '',
                phoneOtherPrefix: '',
                role_in_gym: 'member',
                marketingOptIn: false,
                canManageOwnWorkouts: true,
            });
            setMarketingTagIds([]);
            setSelectedContactId('');
            setSearchFilter('');
            setShowDropdown(false);
            
            await loadRubricaContacts();
            if (refreshBilling) {
                void refreshBilling({ silent: true });
            }
            
        } catch (err) {
            console.error('Errore nella creazione:', err);
            const data = err.response?.data;
            if (data?.error === 'TRIAL_MEMBER_LIMIT') {
                setError(data.message || t('gym.billing.trialMemberLimitError'));
            } else {
                setError(data?.message || data?.error || t('gym.addMember.errorCreatingMember'));
            }
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            email: '',
            name: '',
            phonePrefix: '39',
            phoneNumber: '',
            phoneOtherPrefix: '',
            role_in_gym: 'member',
            marketingOptIn: false,
            canManageOwnWorkouts: true,
        });
        setError(null);
        setSuccess(null);
        setCreatedUser(null);
        setMarketingTagIds([]);
        setSelectedContactId('');
        setSearchFilter('');
        setShowDropdown(false);
    };

    const handleCopyPassword = async () => {
        if (createdUser?.temp_password) {
            try {
                await navigator.clipboard.writeText(createdUser.temp_password);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
                toast.success(t('gym.resetPasswordModal.copiedPassword'));
            } catch (err) {
                console.error('Errore nella copia:', err);
            }
        }
    };

    const handleCopyMessage = async () => {
        if (!credentialsMessage) return;
        try {
            await navigator.clipboard.writeText(credentialsMessage);
            setCopiedMessage(true);
            setTimeout(() => setCopiedMessage(false), 2000);
            toast.success(t('gym.resetPasswordModal.copiedMessage'));
        } catch (err) {
            console.error('Errore nella copia:', err);
        }
    };

    const handleWhatsApp = () => {
        if (!openWhatsApp(createdUser?.phone, credentialsMessage)) {
            toast.error(t('gym.resetPasswordModal.noPhoneHint'));
        }
    };

    const handleTelegram = () => {
        const phoneNorm = normalizePhoneForWhatsApp(createdUser?.phone);
        if (phoneNorm) {
            window.open(`https://t.me/+${phoneNorm}`, '_blank', 'noopener,noreferrer');
        } else {
            toast.error(t('gym.resetPasswordModal.noPhoneHint'));
        }
    };

    // Reinvia email di benvenuto
    const handleResendEmail = async () => {
        if (!createdUser?.user_id) return;
        
        setResendingEmail(true);
        try {
            const api = getAuthenticatedApi();
            const response = await api.post('/gym_members.php?action=resend_welcome_email', {
                user_id: createdUser.user_id
            });
            
            if (response.data.success) {
                // Aggiorna i dati utente con la nuova password
                setCreatedUser({
                    ...createdUser,
                    temp_password: response.data.temp_password,
                    email_sent: true,
                    email_error: null
                });
                toast.success('📧 Email inviata con successo!');
            } else {
                toast.error('❌ Errore nell\'invio dell\'email');
            }
        } catch (err) {
            console.error('Errore reinvio email:', err);
            toast.error('❌ Errore nell\'invio dell\'email');
        } finally {
            setResendingEmail(false);
        }
    };

    // Genera JSON per QR Code
    const getQRCodeValue = () => {
        if (!createdUser) return '';
        return JSON.stringify({
            username: createdUser.username,
            password: createdUser.temp_password,
            timestamp: new Date().toISOString()
        });
    };

    /* Stessi campi della ricerca / filtri in MembersManager (desktop lista clienti) */
    const inputClass =
        'w-full rounded-md border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-white/50 dark:focus:border-indigo-500 dark:focus:ring-white/30';

    return (
        <div className="flex min-h-full w-full flex-col bg-gray-50 dark:bg-gray-900">
            {/* Light: gradient come SidebarLayout / dashboard; Dark: come header Gestione clienti */}
            <div className="shrink-0 bg-gradient-to-r from-[#FDFDFE] to-[#E5DFF1] dark:bg-gradient-to-r dark:from-gray-950 dark:via-gray-900 dark:to-gray-900">
                <div className="px-4 pb-4 pt-5 sm:px-6 sm:pb-5 sm:pt-6">
                    <div className="flex items-start gap-3 sm:gap-4">
                        <Link
                            to="/gym/members"
                            title={t('gym.navigation.members')}
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-gray-200/80 bg-white/90 text-[#3a3a3a] shadow-sm transition hover:bg-white dark:border-white/15 dark:bg-white/10 dark:text-white dark:hover:bg-white/15 sm:h-12 sm:w-12"
                        >
                            <ArrowLeft size={20} className="shrink-0" aria-hidden />
                            <span className="sr-only">{t('gym.navigation.members')}</span>
                        </Link>
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/80 shadow-sm dark:bg-white/10 sm:h-12 sm:w-12">
                            <UserPlus className="text-blue-600 dark:text-gray-300" size={24} />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                            <h1 className="text-2xl font-bold leading-tight text-[#3a3a3a] dark:font-open-sans dark:text-white sm:text-3xl">
                                {t('gym.addMember.title')}
                            </h1>
                            <p className="mt-1 text-sm text-gray-600 dark:font-open-sans dark:font-light dark:text-white/80 sm:text-base">
                                {t('gym.addMember.description')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Area lavoro: come sfondo colonna lista + ricerca (dark #292161), light bianco */}
            <div className="min-h-0 flex-1 overflow-y-auto border-t border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
                <div className="mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
                {error && (
                    <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-800/60 dark:bg-red-950/35">
                        <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
                    </div>
                )}

                {success && (
                    <div className="mb-6 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-emerald-800/50 dark:bg-emerald-950/30">
                        <p className="text-sm text-green-800 dark:text-emerald-200">{success}</p>
                    </div>
                )}

                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-md dark:border-white/10 dark:bg-gray-900 dark:shadow-lg dark:shadow-black/35 sm:p-8">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Role Selection */}
                        <div>
                            <label className="mb-3 block text-sm font-medium text-gray-700 dark:font-open-sans dark:text-white/90">
                                {t('gym.addMember.accountType')}
                            </label>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, role_in_gym: 'member' }))}
                                    className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
                                        formData.role_in_gym === 'member'
                                            ? 'border-blue-500 bg-blue-50 shadow-sm dark:border-white/25 dark:bg-gray-950/90 dark:shadow-md dark:shadow-black/25'
                                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:bg-black/15 dark:hover:border-white/20 dark:hover:bg-black/25'
                                    }`}
                                >
                                    <User
                                        size={24}
                                        className={
                                            formData.role_in_gym === 'member'
                                                ? 'text-blue-600 dark:text-gray-300'
                                                : 'text-gray-400 dark:text-white/40'
                                        }
                                    />
                                    <div className="min-w-0">
                                        <p
                                            className={`font-semibold dark:font-open-sans ${
                                                formData.role_in_gym === 'member'
                                                    ? 'text-blue-800 dark:text-white'
                                                    : 'text-gray-800 dark:text-white/85'
                                            }`}
                                        >
                                            {t('gym.addMember.member')}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-white/60">
                                            {t('gym.addMember.memberDescription')}
                                        </p>
                                    </div>
                                </button>

                                <button
                                    type="button"
                                    onClick={() => setFormData((prev) => ({ ...prev, role_in_gym: 'trainer' }))}
                                    className={`flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
                                        formData.role_in_gym === 'trainer'
                                            ? 'border-emerald-500 bg-emerald-50 shadow-sm dark:border-emerald-300/40 dark:bg-gray-950/90 dark:shadow-md dark:shadow-black/25'
                                            : 'border-gray-200 hover:border-gray-300 dark:border-white/10 dark:bg-black/15 dark:hover:border-white/20 dark:hover:bg-black/25'
                                    }`}
                                >
                                    <Users
                                        size={24}
                                        className={
                                            formData.role_in_gym === 'trainer'
                                                ? 'text-emerald-600 dark:text-emerald-300'
                                                : 'text-gray-400 dark:text-white/40'
                                        }
                                    />
                                    <div className="min-w-0">
                                        <p
                                            className={`font-semibold dark:font-open-sans ${
                                                formData.role_in_gym === 'trainer'
                                                    ? 'text-emerald-800 dark:text-white'
                                                    : 'text-gray-800 dark:text-white/85'
                                            }`}
                                        >
                                            {t('gym.addMember.trainer')}
                                        </p>
                                        <p className="text-sm text-gray-500 dark:text-white/60">
                                            {t('gym.addMember.trainerDescription')}
                                        </p>
                                    </div>
                                </button>
                            </div>
                        </div>

                        {/* Rubrica Selection */}
                        {rubricaContacts.length > 0 && (
                            <div className="mb-6 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                                <label className="mb-2 flex items-center gap-2 text-sm font-medium text-gray-700 dark:font-open-sans dark:text-white/90">
                                    <BookOpen size={18} className="text-blue-600 dark:text-gray-300" />
                                    Seleziona dalla Rubrica (opzionale)
                                </label>
                                
                                {/* Search Filter con Dropdown */}
                                <div className="relative" ref={dropdownRef}>
                                    <div className="relative mb-3">
                                        <Search className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-gray-400 dark:text-white/50" aria-hidden />
                                        <input
                                            ref={searchInputRef}
                                            type="text"
                                            value={searchFilter}
                                            onChange={(e) => {
                                                setSearchFilter(e.target.value);
                                                setShowDropdown(true);
                                            }}
                                            onFocus={() => setShowDropdown(true)}
                                            placeholder="Cerca per nome, cognome o email..."
                                            className={`${inputClass} py-2.5 pl-10`}
                                        />
                                    </div>
                                    
                                    {/* Dropdown Results */}
                                    {showDropdown && filteredContacts.length > 0 && (
                                        <div className="absolute z-10 mt-1 max-h-60 w-full overflow-y-auto rounded-md border border-gray-300 bg-white shadow-lg dark:border-gray-600 dark:bg-gray-800">
                                            {filteredContacts.map((contact) => {
                                                const displayName = [contact.nome, contact.cognome]
                                                    .filter(Boolean)
                                                    .join(' ')
                                                    .trim() || 'Nome non disponibile';
                                                const displayEmail = contact.email || 'Nessuna email';
                                                
                                                return (
                                                    <button
                                                        key={contact.id}
                                                        type="button"
                                                        onClick={() => handleContactSelect(contact)}
                                                        className="w-full border-b border-gray-200 px-4 py-3 text-left transition-colors last:border-b-0 hover:bg-blue-50 dark:border-gray-700 dark:hover:bg-gray-800/50"
                                                    >
                                                        <div className="font-medium text-gray-900 dark:text-white">
                                                            {displayName}
                                                        </div>
                                                        <div className="text-sm text-gray-600 dark:text-white/60">
                                                            {displayEmail}
                                                        </div>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                    
                                    {/* Nessun risultato */}
                                    {showDropdown && searchFilter.trim() && filteredContacts.length === 0 && (
                                        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-300 bg-white p-4 shadow-lg dark:border-gray-600 dark:bg-gray-800">
                                            <p className="text-sm text-gray-600 dark:text-white/70">
                                                Nessun contatto trovato con "{searchFilter}"
                                            </p>
                                        </div>
                                    )}
                                </div>
                                
                                {/* Info e contatore */}
                                <p className="mt-2 text-xs text-gray-600 dark:text-white/55">
                                    {searchFilter.trim() 
                                        ? filteredContacts.length > 0 
                                            ? `${filteredContacts.length} risultato/i trovato/i - Clicca per selezionare`
                                            : 'Nessun contatto trovato'
                                        : `${rubricaContacts.length} contatti disponibili - Digita per cercare`}
                                </p>
                                
                                {/* Contatto selezionato */}
                                {selectedContactId && (
                                    <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 dark:border-emerald-500/30 dark:bg-emerald-950/25">
                                        <p className="text-sm font-medium text-green-800 dark:text-emerald-200">
                                            ✓ Contatto selezionato - Campi compilati automaticamente
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Form Fields */}
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:font-open-sans dark:text-white/90">
                                    {t('gym.addMember.form.fullName')} *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    className={inputClass}
                                    placeholder={t('gym.addMember.form.fullNamePlaceholder')}
                                />
                            </div>

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:font-open-sans dark:text-white/90">
                                    {t('gym.addMember.form.email')} *
                                </label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    className={inputClass}
                                    placeholder={t('gym.addMember.form.emailPlaceholder')}
                                />
                            </div>
                        </div>

                        {/* Telefono opzionale: prefisso + numero (per WhatsApp/Telegram) */}
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700 dark:font-open-sans dark:text-white/90">
                                {t('gym.addMember.form.phone')}
                            </label>
                            <div className="flex flex-wrap items-center gap-2">
                                <div className="w-40 flex-shrink-0">
                                    <select
                                        name="phonePrefix"
                                        value={formData.phonePrefix}
                                        onChange={handleChange}
                                        className={inputClass}
                                    >
                                        {PHONE_PREFIXES.map((p) => (
                                            <option key={p.code} value={p.code}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                                {formData.phonePrefix === 'other' && (
                                    <input
                                        type="tel"
                                        name="phoneOtherPrefix"
                                        value={formData.phoneOtherPrefix}
                                        onChange={handleChange}
                                        placeholder="+XX"
                                        className={`${inputClass} w-24 px-2`}
                                    />
                                )}
                                <input
                                    type="tel"
                                    name="phoneNumber"
                                    value={formData.phoneNumber}
                                    onChange={handleChange}
                                    className={`${inputClass} min-w-[180px] flex-1`}
                                    placeholder={t('gym.addMember.form.phoneNumberPlaceholder')}
                                />
                            </div>
                            <p className="mt-1 text-xs text-gray-500 dark:text-white/50">
                                {t('gym.addMember.form.phoneHint')}
                            </p>
                        </div>

                        {formData.role_in_gym === 'member' && (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-800/40">
                                <label className="flex cursor-pointer items-start gap-3">
                                    <input
                                        type="checkbox"
                                        name="marketingOptIn"
                                        checked={formData.marketingOptIn}
                                        onChange={handleChange}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-white/30 dark:bg-gray-800"
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                            {t('gym.addMember.form.marketingOptIn')}
                                        </span>
                                        <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                            {t('gym.addMember.form.marketingOptInHint')}
                                        </span>
                                    </span>
                                </label>

                                {formData.marketingOptIn && (
                                    <div className="mt-4 border-t border-gray-200 pt-4 dark:border-white/10">
                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                            {t('gym.addMember.form.marketingTagsTitle')}
                                        </p>
                                        <p className="mt-1 text-xs text-gray-500 dark:text-white/60">
                                            {t('gym.addMember.form.marketingTagsHint')}
                                        </p>
                                        {loadingEmailTags ? (
                                            <p className="mt-2 text-xs text-gray-500 dark:text-white/50">
                                                {t('gym.addMember.form.marketingTagsLoading')}
                                            </p>
                                        ) : customEmailTags.length === 0 ? (
                                            <p className="mt-2 text-xs text-gray-500 dark:text-white/60">
                                                {t('gym.addMember.form.marketingTagsEmpty')}{' '}
                                                <Link
                                                    to="/gym/email"
                                                    className="font-medium text-indigo-600 hover:underline dark:text-[#cba5fa]"
                                                >
                                                    {t('gym.addMember.form.marketingTagsManageLink')}
                                                </Link>
                                            </p>
                                        ) : (
                                            <div className="mt-3 flex flex-wrap gap-2">
                                                {customEmailTags.map((tag) => {
                                                    const active = marketingTagIds.includes(tag.id);
                                                    return (
                                                        <button
                                                            key={tag.id}
                                                            type="button"
                                                            onClick={() => {
                                                                setMarketingTagIds((prev) =>
                                                                    active
                                                                        ? prev.filter((id) => id !== tag.id)
                                                                        : [...prev, tag.id]
                                                                );
                                                            }}
                                                            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                                                                active
                                                                    ? 'border-indigo-400 bg-indigo-100 text-indigo-900 dark:border-[#cba5fa]/50 dark:bg-indigo-900/50 dark:text-indigo-100'
                                                                    : 'border-gray-300 bg-white text-gray-700 dark:border-white/20 dark:bg-gray-800/50 dark:text-white/85'
                                                            }`}
                                                        >
                                                            <Tag className="h-3 w-3 opacity-70" aria-hidden />
                                                            {tag.name}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {formData.role_in_gym === 'member' && (
                            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-800/40">
                                <label className="flex cursor-pointer items-start gap-3">
                                    <input
                                        type="checkbox"
                                        name="canManageOwnWorkouts"
                                        checked={formData.canManageOwnWorkouts}
                                        onChange={handleChange}
                                        className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-white/30 dark:bg-gray-800"
                                    />
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm font-medium text-gray-900 dark:text-white">
                                            {t('gym.addMember.form.canManageOwnWorkouts')}
                                        </span>
                                        <span className="mt-1 block text-xs text-gray-500 dark:text-white/60">
                                            {t('gym.addMember.form.canManageOwnWorkoutsHint')}
                                        </span>
                                    </span>
                                </label>
                            </div>
                        )}

                        {/* Informazione Generazione Automatica */}
                        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-gray-700 dark:bg-gray-800/40">
                            <div className="flex items-center gap-2 text-blue-900 dark:text-gray-300">
                                <User size={16} className="shrink-0" />
                                <span className="font-semibold dark:font-open-sans">Generazione Automatica</span>
                            </div>
                            <p className="mt-2 text-sm text-blue-800 dark:text-white/75">
                                Username e password verranno generati automaticamente dal nome completo. Al primo accesso l&apos;utente dovrà solo cambiare la password temporanea.
                            </p>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4 sm:pt-4">
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex min-h-[48px] flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 py-3.5 font-medium text-white shadow-md shadow-black/10 transition hover:bg-blue-700 disabled:opacity-50 dark:bg-gray-900 dark:font-open-sans dark:text-gray-300 dark:shadow-black/25 dark:hover:brightness-110"
                            >
                                {loading ? (
                                    <LoadingSpinner size="small" />
                                ) : (
                                    <>
                                        <Save size={20} />
                                        {formData.role_in_gym === 'trainer' ? t('gym.addMember.buttons.createTrainer') : 
                                         formData.role_in_gym === 'manager' ? t('gym.addMember.buttons.createManager') : t('gym.addMember.buttons.createMember')}
                                    </>
                                )}
                            </button>
                            
                            <button
                                type="button"
                                onClick={resetForm}
                                className="flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-gray-300 bg-gray-100 px-6 py-3.5 font-medium text-gray-800 transition hover:bg-gray-200 dark:border-gray-700 dark:bg-gray-800 dark:font-open-sans dark:text-white dark:hover:bg-gray-700"
                            >
                                <X size={20} />
                                {t('gym.addMember.buttons.reset')}
                            </button>
                        </div>
                    </form>
                </div>
                </div>
            </div>

            {/* Modal credenziali - stesso layout orizzontale (md+) del reset password post-successo */}
            {createdUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                    <div className="relative mx-4 flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900 dark:shadow-black/35">
                        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-[#dad8e5] px-5 py-4 pr-12 dark:border-white/10 dark:bg-gray-800">
                            <div className="flex min-w-0 items-center gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-emerald-900/40">
                                    <Key className="h-5 w-5 text-green-600 dark:text-emerald-300" />
                                </div>
                                <div className="min-w-0">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:font-open-sans dark:text-white">
                                        Utente Creato con Successo!
                                    </h3>
                                    <p className="truncate text-sm text-gray-600 dark:text-white/70">{createdUser.name}</p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setCreatedUser(null)}
                                className="absolute right-3 top-3 rounded-lg p-2 text-gray-500 transition-colors hover:bg-black/5 dark:text-white/80 dark:hover:bg-white/10"
                                aria-label="Chiudi"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto bg-white px-6 py-6 dark:bg-gray-900">
                            <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 dark:border-white/10 dark:bg-gray-900">
                                <p className="text-center text-sm text-blue-800 dark:text-white/85">
                                    L&apos;utente dovrà utilizzare questa password per il primo accesso
                                </p>
                            </div>

                            <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
                                <div className="flex flex-col items-center md:w-[min(100%,260px)] md:shrink-0">
                                    <div className="relative rounded-lg border-2 border-orange-200 bg-white p-3 dark:bg-white">
                                        <QRCodeSVG value={getQRCodeValue()} size={200} level="H" marginSize={4} />
                                    </div>
                                    <p className="mt-2 max-w-[220px] text-center text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                                        📱 Inquadra con l&apos;app per accedere rapidamente
                                    </p>
                                </div>

                                <div className="flex min-w-0 flex-1 flex-col gap-4">
                                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-gray-900">
                                        <div className="space-y-4">
                                            <div>
                                                <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Username:</p>
                                                <p className="break-all font-mono text-base text-gray-900 dark:text-white sm:text-lg">
                                                    {createdUser.username}
                                                </p>
                                            </div>
                                            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                                                <div className="min-w-0 flex-1">
                                                    <p className="mb-1 text-sm text-gray-500 dark:text-gray-400">Password Temporanea:</p>
                                                    <p className="break-all font-mono text-base text-gray-900 dark:text-white sm:text-lg">
                                                        {createdUser.temp_password}
                                                    </p>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleCopyPassword}
                                                    className="flex shrink-0 items-center justify-center gap-2 self-start rounded-lg bg-blue-600 px-4 py-2.5 text-sm text-white transition-colors hover:bg-blue-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110 sm:self-auto"
                                                >
                                                    {copied ? (
                                                        <>
                                                            <Check size={16} />
                                                            Copiato!
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Copy size={16} />
                                                            Copia
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                            {t('gym.resetPasswordModal.communicateTitle')}
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <button
                                                type="button"
                                                onClick={handleCopyMessage}
                                                disabled={!credentialsMessage}
                                                className="flex items-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-800 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-white dark:hover:brightness-110"
                                            >
                                                {copiedMessage ? <Check size={16} /> : <Copy size={16} />}
                                                {copiedMessage ? t('gym.resetPasswordModal.copiedMessage') : t('gym.resetPasswordModal.copyMessage')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleWhatsApp}
                                                title={createdUser?.phone ? '' : t('gym.resetPasswordModal.noPhoneHint')}
                                                className="flex items-center gap-2 rounded-md bg-[#25D366] px-4 py-2 text-sm text-white transition-colors hover:bg-[#20BD5A]"
                                            >
                                                <MessageCircle size={16} />
                                                {t('gym.resetPasswordModal.sendWhatsApp')}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleTelegram}
                                                title={createdUser?.phone ? '' : t('gym.resetPasswordModal.noPhoneHint')}
                                                className="flex items-center gap-2 rounded-md bg-[#0088cc] px-4 py-2 text-sm text-white transition-colors hover:bg-[#006699]"
                                            >
                                                <Send size={16} />
                                                {t('gym.resetPasswordModal.sendTelegram')}
                                            </button>
                                        </div>
                                        {!createdUser?.phone && (
                                            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                                {t('gym.resetPasswordModal.noPhoneHint')}
                                            </p>
                                        )}
                                    </div>

                                    {createdUser.email_sent ? (
                                        <div className="rounded-xl border border-green-200 bg-green-50 p-4 dark:border-emerald-500/25 dark:bg-emerald-950/25">
                                            <div className="flex items-start gap-3">
                                                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-600 dark:text-green-400" />
                                                <div className="min-w-0 flex-1">
                                                    <h5 className="mb-1 text-sm font-semibold text-green-900 dark:text-green-100">
                                                        📧 Email di Benvenuto Inviata!
                                                    </h5>
                                                    <p className="text-sm text-green-700 dark:text-green-300">
                                                        L&apos;utente ha ricevuto un&apos;email con le credenziali di accesso e i link per scaricare l&apos;app iOS/Android.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-950/20">
                                            <div className="flex items-start gap-3">
                                                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                                                <div className="min-w-0 flex-1">
                                                    <h5 className="mb-1 text-sm font-semibold text-amber-900 dark:text-amber-100">
                                                        📧 Email di benvenuto non disponibile
                                                    </h5>
                                                    <p className="mb-3 text-sm text-amber-700 dark:text-amber-300">
                                                        {createdUser.email_error ||
                                                            'L\'invio email non è configurato. Usa "Invia via WhatsApp" o "Copia messaggio" per inviare le credenziali al cliente.'}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={handleResendEmail}
                                                        disabled={resendingEmail}
                                                        className="flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:bg-amber-400"
                                                    >
                                                        {resendingEmail ? (
                                                            <>
                                                                <RefreshCw size={16} className="animate-spin" />
                                                                Invio in corso...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Mail size={16} />
                                                                Reinvia Email
                                                            </>
                                                        )}
                                                    </button>
                                                    <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
                                                        Se l&apos;invio email è configurato sul server, questo pulsante genererà una nuova password e proverà a reinviare.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {createdUser.email_sent && (
                                        <div className="rounded-lg border border-gray-200 bg-white/80 p-3 text-center dark:border-white/10 dark:bg-gray-800/30">
                                            <button
                                                type="button"
                                                onClick={handleResendEmail}
                                                disabled={resendingEmail}
                                                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700 disabled:opacity-50 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                            >
                                                {resendingEmail ? (
                                                    <>
                                                        <RefreshCw size={16} className="animate-spin" />
                                                        Invio in corso...
                                                    </>
                                                ) : (
                                                    <>
                                                        <RefreshCw size={16} />
                                                        Reinvia Email
                                                    </>
                                                )}
                                            </button>
                                            <p className="mt-2 text-xs text-gray-500 dark:text-white/50">
                                                Genera nuova password e reinvia email
                                            </p>
                                        </div>
                                    )}

                                    <div className="flex justify-end border-t border-gray-200 pt-2 dark:border-white/10">
                                        <button
                                            type="button"
                                            onClick={() => setCreatedUser(null)}
                                            className="rounded-xl bg-gray-600 px-6 py-2.5 font-medium text-white transition-colors hover:bg-gray-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
                                        >
                                            Chiudi
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


